import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate } from "react-router-dom";
import { AddButton } from "../../../../utils/Custom/Button";
import { submitSiteDelete } from "../../../../utils/commonComponent/delete";
import { fetchQuery } from "../../../../utils/commonComponent/fetched";
import ListSearchSection from "../../../../utils/Custom/ListSearchSection";
import ListView from "../../../../utils/Custom/listView";
import { getPermissions } from "../../../Pages/rbac/hasAccess";

const headers = [
  "id",
  "CandId",
  "candidateName",
  "discipline",
  "totalExp",
  "currentEmployer",
  "currentDesignation",
  "currentLocation",
  "mobileNo",
  "emaiId",
];

const fields = [
  { value: "candidateName", label: "Candidate Name" },
  { value: "CandId", label: "Cand ID" },
  { value: "discipline", label: "Discipline" },
  { value: "mobileNo", label: "Mobile No." },
];

const CandidateList = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = getPermissions(props.moduleId);

  const handleEdit = (row) => navigate(`/talent-acquisition/editCandidate/${row.id}`);
  const handleDelete = (row) => submitSiteDelete("talentAcqCandidate", row.id);

  const [candidateList, setCandidateList] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [allCandidatesData, setAllCandidatesData] = useState([]);
  const [searchBy, setSearchBy] = React.useState(sessionStorage.getItem("searchBy") || "candidateName");
  const [searchKey, setSearchKey] = React.useState(sessionStorage.getItem("searchKey") || "");
  const [searched, setSearched] = React.useState(sessionStorage.getItem("searched") === "true" || false);
  const [currentPage, setCurrentPage] = useState(() => Number(sessionStorage.getItem("ListPageCount") || 0));
  const [rowsPerPage, setRowsPerPage] = useState(() => Number(sessionStorage.getItem("ListRowsPerPage") || 30));

  const handlePaginationChange = (page, rows) => {
    setCurrentPage(page);
    setRowsPerPage(rows);
    sessionStorage.setItem("currentPath", location.pathname);
    sessionStorage.setItem("ListPageCount", page);
    sessionStorage.setItem("ListRowsPerPage", rows);
  };

  const getWhereClause = () => {
    const key = searchKey.trim();
    let whereConditions = ["status = 1"];

    if (searchBy && key) {
      if (searchBy === "discipline") {
        whereConditions.push(
          `discipline IN (SELECT id FROM talentAcquisitionMaster WHERE type = 1 AND Value LIKE '%${key}%')`
        );
      } else {
        whereConditions.push(`${searchBy} LIKE '%${key}%'`);
      }
    }

    return `WHERE ${whereConditions.join(" AND ")}`;
  };

  const getDataQuery = () => {
    const whereClause = getWhereClause();
    const pagination =
      rowsPerPage !== -1
        ? `LIMIT ${rowsPerPage} OFFSET ${rowsPerPage * currentPage}`
        : "";
    return `SELECT * FROM talentAcqCandidate ${whereClause} ORDER BY id DESC ${pagination};`;
  };

  const getCountQuery = () => {
    return `SELECT COUNT(*) AS totalRows FROM talentAcqCandidate ${getWhereClause()};`;
  };

  const getAllDataQuery = () => {
    const whereClause = getWhereClause();
    return `SELECT * FROM talentAcqCandidate ${whereClause} ORDER BY id DESC;`;
  };

  const fetchData = async () => {
    try {
      const [data, countResult, masters, allData] = await Promise.all([
        fetchQuery(getDataQuery()),
        fetchQuery(getCountQuery()),
        fetchQuery("SELECT id, Value FROM talentAcquisitionMaster WHERE type = 1"),
        fetchQuery(getAllDataQuery()),
      ]);

      const masterList = masters || [];

      const mapped = (data || []).map((row) => ({
        ...row,
        discipline: masterList.find((m) => m.id == row.discipline)?.Value || row.discipline || "",
      }));

      const mappedAll = (allData || []).map((row) => ({
        CandId: row.CandId || "",
        candidateName: row.candidateName || "",
        gender: row.gender || "",
        discipline: masterList.find((m) => m.id == row.discipline)?.Value || row.discipline || "",
        education: row.education || "",
        certifications: row.certifications || "",
        totalExp: row.totalExp ?? "",
        tpiExp: row.tpiExp ?? "",
        currentEmployer: row.currentEmployer || "",
        currentDesignation: row.currentDesignation || "",
        currentCtc: row.currentCtc ?? "",
        expectedCtc: row.expectedCtc ?? "",
        noticePeriod: row.noticePeriod || "",
        currentLocation: row.currentLocation || "",
        mobileNo: row.mobileNo || "",
        emailId: row.emaiId || "",
      }));

      setCandidateList(mapped);
      setTotalRows(countResult?.[0]?.totalRows || 0);
      setAllCandidatesData(mappedAll);
    } catch (error) {
      console.error("Error fetching candidate data:", error);
    }
  };

  const handleSearch = () => {
    if (!searchBy || !searchKey.trim()) {
      alert("Please select a search field and enter a search value.");
      return;
    }
    fetchData();
    setSearched(true);
    sessionStorage.setItem("searchBy", searchBy);
    sessionStorage.setItem("searchKey", searchKey);
    sessionStorage.setItem("searched", "true");
    sessionStorage.setItem("currentPath", location.pathname);
    sessionStorage.setItem("ListPageCount", 0);
    setCurrentPage(0);
  };

  const clearSearch = () => {
    setSearched(false);
    setSearchBy("candidateName");
    setSearchKey("");
    sessionStorage.removeItem("ListPageCount");
    sessionStorage.removeItem("ListRowsPerPage");
    sessionStorage.removeItem("searchBy");
    sessionStorage.removeItem("searchKey");
    sessionStorage.removeItem("searched");
    setCurrentPage(0);
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, rowsPerPage]);

  useEffect(() => {
    if (searched === false) {
      fetchData();
    }
  }, [searched]);

  return (
    <>
      {(permissions.create || permissions.allAccess) && (
        <AddButton
          id="btn-add-candidate"
          to="/talent-acquisition/editCandidate/0"
          label="Add Candidate"
          icon={<AddIcon fontSize="small" />}
        />
      )}
      <ListSearchSection
        title="Search Candidates"
        fields={fields}
        onSearch={handleSearch}
        searchKey={searchKey}
        setSearchKey={setSearchKey}
        searchBy={searchBy}
        setSearchBy={setSearchBy}
        clearSearch={clearSearch}
        searched={searched}
        setSearched={setSearched}
        showExcelDownload={permissions.excelDownload || permissions.allAccess}
        finalData={allCandidatesData}
        notNeededColumn={[]}
        excelFileName="Candidate List"
      />
      <ListView
        headers={headers}
        data={candidateList}
        actions={{
          edit: handleEdit,
          delete: handleDelete,
        }}
        showCheckbox={true}
        deleteTableName="talentAcqCandidate"
        moduleId={props.moduleId}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalRows={totalRows}
        onPaginationChange={handlePaginationChange}
      />
    </>
  );
};

export default CandidateList;
